import { createSlice, createSelector } from "@reduxjs/toolkit";
import { RootState } from "../index";

type UIRole =
  | "customer_owner"
  | "customer_team_lead"
  | "customer_team_member"
  | "expert_owner"
  | "expert_team_lead"
  | "expert_team_member"
  | "supporty_admin"
  | null;

interface UIState {
  uiRole: UIRole;
}

const initialState: UIState = {
  uiRole: null,
};

const uiSlice = createSlice({
  name: "ui",
  initialState,
  reducers: {
    setUIRole: (state, action) => {
      state.uiRole = action.payload;
    },
  },
});

export const { setUIRole } = uiSlice.actions;
export default uiSlice.reducer;

// Selector to determine UI role based on user role, organization plan type, and team role
export const selectUIRole = createSelector(
  [
    (state: RootState) => state?.user?.userData,
    (state: RootState) => state?.organization?.currentOrganization,
    (state: RootState) => state?.team?.currentTeam,
  ],
  (userData, currentOrg, currentTeam) => {
    if (!userData) return null;

    // Check for supporty_admin role first
    if (userData.role === "supporty_admin") {
      return "supporty_admin";
    }

    // For regular users, organization and team are required
    if (!currentOrg || !currentTeam) {
      return null;
    }

    const userId = userData.uid;

    // Find user's role in organization
    const userOrgRecord = currentOrg.users.find(
      (user) => user.userId === userId
    );
    if (!userOrgRecord) return null;

    // Find user's role in team
    const userTeamRecord = currentTeam.users.find(
      (user) => user.userId === userId
    );
    if (!userTeamRecord && userOrgRecord.role !== "owner") return null;

    const orgPlanType = currentOrg.planType;
    const userOrgRole = userOrgRecord.role;
    const userTeamRole = userTeamRecord?.role;

    if (orgPlanType === "customer") {
      if (userOrgRole === "owner") return "customer_owner";
      if (userOrgRole === "member") {
        if (userTeamRole === "team_lead") return "customer_team_lead";
        if (userTeamRole === "team_member") return "customer_team_member";
      }
    } else if (orgPlanType === "expert") {
      if (userOrgRole === "owner") return "expert_owner";
      if (userOrgRole === "member") {
        if (userTeamRole === "team_lead") return "expert_team_lead";
        if (userTeamRole === "team_member") return "expert_team_member";
      }
    }

    return null;
  }
);
